import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Compass, Heart, MessageCircle, Wallet, Bell, User, Shield, LogOut, Menu, X, BadgeCheck, HelpCircle,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useWs } from "../context/WsContext";
import { BrandLogo } from "./BrandLogo";
import api from "../lib/api";

const NAV = [
  { to: "/discover", label: "Explore", icon: Compass, testid: "nav-explore-link" },
  { to: "/offers", label: "Offers", icon: Heart, testid: "nav-offers-link" },
  { to: "/messages", label: "Messages", icon: MessageCircle, testid: "nav-messages-link" },
  { to: "/favorites", label: "Favorites", icon: Heart, testid: "nav-favorites-link" },
  { to: "/wallet", label: "Credits", icon: Wallet, testid: "nav-wallet-link" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { subscribe } = useWs();
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadNotif, setUnreadNotif] = useState(0);
  const [unreadMsg, setUnreadMsg] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [lowBalance, setLowBalance] = useState(null);
  const [nudgeDismissedAt, setNudgeDismissedAt] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);

  const loadPending = async () => {
    if (user?.role !== "admin") return;
    try { const { data } = await api.get("/admin/pending-counts"); setPendingCount(data.actions_total ?? data.total ?? 0); } catch (e) {}
  };
  useEffect(() => { loadPending(); }, [location.pathname, user?.role]);
  useEffect(() => {
    const h = () => loadPending();
    window.addEventListener("admin-stats-refresh", h);
    return () => window.removeEventListener("admin-stats-refresh", h);
  }, [user?.role]);

  const loadCredits = async () => {
    if (!user) return;
    try {
      const [b, c] = await Promise.all([api.get("/credits/balance"), api.get("/credits/catalog")]);
      const threshold = c.data.low_balance_threshold ?? 0;
      if (threshold > 0 && b.data.credits <= threshold) {
        setLowBalance((prev) => {
          if (prev && prev.balance !== b.data.credits) setNudgeDismissedAt(null); // balance changed & still low -> re-show
          return { balance: b.data.credits, threshold };
        });
      } else { setLowBalance(null); setNudgeDismissedAt(null); }
    } catch (e) {}
  };
  useEffect(() => { loadCredits(); }, [location.pathname]);

  const loadCounts = async () => {
    if (!user) return;
    try {
      const [n, c] = await Promise.all([api.get("/notifications"), api.get("/connections")]);
      setUnreadNotif(n.data.unread || 0);
      setUnreadMsg((c.data.connections || []).reduce((a, x) => a + (x.unread || 0), 0));
    } catch (e) {}
  };

  useEffect(() => { loadCounts(); }, [location.pathname]);

  useEffect(() => {
    const unsub = subscribe((evt) => {
      if (evt.event === "notification") {
        setUnreadNotif((v) => v + 1);
        toast(evt.payload.title, { description: evt.payload.body });
      }
      if (evt.event === "message") {
        if (!location.pathname.startsWith("/messages")) setUnreadMsg((v) => v + 1);
      }
    });
    return unsub;
  }, [subscribe, location.pathname]);

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <div className="db-grain min-h-screen relative" style={{ background: "radial-gradient(1200px 600px at 80% -10%, rgba(117,26,50,0.22), transparent 60%), #0C0A0A" }}>
      <nav className="sticky top-0 z-50 db-glass border-b border-[#D4AF37]/15 px-4 sm:px-6 py-3.5 flex items-center justify-between">
        <Link to="/discover" data-testid="nav-brand-logo" className="flex items-center group">
          <BrandLogo variant="wordmark" className="h-9 sm:h-11 w-auto transition-transform duration-300 group-hover:scale-105" />
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={n.testid}
              className={`relative px-4 py-2 rounded-full text-sm font-medium transition-colors ${isActive(n.to) ? "text-[#D4AF37] bg-[#541224]/40" : "text-[#D8D2C9] hover:text-[#FBF8F3]"}`}>
              {n.label}
              {n.to === "/messages" && unreadMsg > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#751A32] text-white text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center">{unreadMsg}</span>
              )}
            </Link>
          ))}
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin-dashboard-link"
              className={`relative flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${isActive("/admin") ? "text-[#0C0A0A] bg-[#D4AF37] border-[#D4AF37]" : "text-[#D4AF37] border-[#D4AF37]/40 hover:border-[#D4AF37] hover:bg-[#541224]/40"}`}>
              <Shield size={15} /> Admin Dashboard
              {pendingCount > 0 && (
                <span data-testid="admin-pending-badge" onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate("/admin"); }} title="Open Admin Action Center" className="ml-0.5 bg-[#751A32] text-white text-[10px] font-bold rounded-full h-5 min-w-5 px-1.5 flex items-center justify-center cursor-pointer hover:bg-[#8B263E]">{pendingCount}</span>
              )}
            </Link>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Link to="/notifications" data-testid="nav-notifications-link" className="relative p-2 rounded-full hover:bg-[#1C171A] text-[#D8D2C9]">
            <Bell size={20} />
            {unreadNotif > 0 && <span className="absolute top-0 right-0 bg-[#D4AF37] text-[#0C0A0A] text-[10px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center">{unreadNotif}</span>}
          </Link>
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin-link-icon" className="lg:hidden relative p-2 rounded-full hover:bg-[#1C171A] text-[#D4AF37]" title="Admin Dashboard">
              <Shield size={20} />
              {pendingCount > 0 && <span className="absolute top-0 right-0 bg-[#751A32] text-white text-[10px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center">{pendingCount}</span>}
            </Link>
          )}
          <Link to="/me" data-testid="nav-profile-link" className="hidden sm:flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full bg-[#1C171A] border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-colors">
            <span className="h-7 w-7 rounded-full bg-[#541224] flex items-center justify-center text-[#D4AF37]"><User size={15} /></span>
            <span className="text-sm text-[#FBF8F3] max-w-[90px] truncate">{user?.display_name}</span>
          </Link>
          <button onClick={async () => { await logout(); navigate("/auth", { replace: true }); }} data-testid="nav-logout-btn" className="p-2 rounded-full hover:bg-[#1C171A] text-[#9E978E] hover:text-[#751A32]" title="Sign out"><LogOut size={18} /></button>
          <button onClick={() => setMobileOpen((v) => !v)} data-testid="mobile-menu-toggle" className="lg:hidden p-2 text-[#D8D2C9]">{mobileOpen ? <X size={22} /> : <Menu size={22} />}</button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="lg:hidden db-glass border-b border-[#D4AF37]/15 px-4 py-3 flex flex-col gap-1 relative z-40">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl ${isActive(n.to) ? "text-[#D4AF37] bg-[#541224]/40" : "text-[#D8D2C9]"}`}>
              <n.icon size={18} /> {n.label}
            </Link>
          ))}
          <Link to="/me" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#D8D2C9]"><User size={18} /> My Profile</Link>
          {user?.role === "admin" && (
            <Link to="/admin?section=profiles" data-testid="nav-admin-dashboard-link-mobile" onClick={() => setMobileOpen(false)} className="flex items-center justify-between px-4 py-3 rounded-xl text-[#D4AF37] bg-[#541224]/30 font-semibold">
              <span className="flex items-center gap-3"><Shield size={18} /> Admin Dashboard</span>
              {pendingCount > 0 && <span data-testid="admin-pending-badge-mobile" className="bg-[#751A32] text-white text-[10px] font-bold rounded-full h-5 min-w-5 px-1.5 flex items-center justify-center">{pendingCount}</span>}
            </Link>
          )}
          <Link to="/credits/how" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#D8D2C9]"><HelpCircle size={18} /> How Credits Work</Link>
          <Link to="/verify" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#D8D2C9]"><BadgeCheck size={18} /> Verification</Link>
        </div>
      )}

      <main className="relative z-10">{children}</main>

      {lowBalance && nudgeDismissedAt !== lowBalance.balance && !location.pathname.startsWith("/wallet") && (
        <div data-testid="low-balance-nudge" className="fixed bottom-3 right-3 z-40 db-glass rounded-full border border-[#D4AF37]/30 pl-3 pr-2 py-1.5 db-fade-up shadow-lg flex items-center gap-2 max-w-[calc(100vw-1.5rem)]">
          <Wallet size={14} className="text-[#D4AF37] shrink-0" />
          <span className="text-xs text-[#D8D2C9] whitespace-nowrap">Low credits: <span data-testid="nudge-balance" className="text-[#D4AF37] font-medium">{lowBalance.balance}</span></span>
          <Link to="/wallet" data-testid="nudge-buy-link" onClick={() => setNudgeDismissedAt(lowBalance.balance)} className="text-xs rounded-full px-2.5 py-0.5 bg-[#541224] border border-[#D4AF37]/40 text-[#D4AF37] hover:border-[#D4AF37] whitespace-nowrap">Top Up</Link>
          <button data-testid="nudge-dismiss" aria-label="Dismiss" onClick={() => setNudgeDismissedAt(lowBalance.balance)} className="text-[#9E978E] hover:text-[#FBF8F3] shrink-0"><X size={14} /></button>
        </div>
      )}
    </div>
  );
}
